import { researchConditionPending } from "./researchReview";
import { channelDraftDifferences, hasStockDifference, needsChannelPhotos, isNewChannelListing } from "./channelActivity";
import { inventoryAge, inventoryAgeDays, inventoryAgeLabel } from "./inventoryFacts";
import { stockReviewIsDue } from "./stock";
import { marketplaceLabels, saleStatusLabels } from "./labels";
import { activeWorkspace, ageDays, currentUser, workspaceItems, workspaceListings, workspaceSales } from "./logic";
import type { InventoryItem, MarketplaceListing, ReOSDatabase, ReOSNotification, Sale } from "./types";

const SYSTEM_NOTIFICATION_PREFIX = "system_";
const LEGACY_DEMO_NOTIFICATION_IDS = new Set(["notification_aging", "notification_sale", "notification_drafts"]);

export function syncNotifications(database: ReOSDatabase, now = new Date()): ReOSDatabase {
  const workspace = activeWorkspace(database);
  const user = currentUser(database);
  if (!workspace || !user) {
    return {
      ...database,
      notifications: database.notifications.filter((notification) => !LEGACY_DEMO_NOTIFICATION_IDS.has(notification.id))
    };
  }

  const generated = generateSystemNotifications(database, now);
  const existingByID = new Map<string, ReOSNotification>();
  database.notifications.forEach((notification) => {
    userNotificationKeys(notification).forEach((key) => existingByID.set(key, notification));
  });
  const generatedWithReadState = generated.map((notification) => ({
    ...notification,
    readAt: firstNotificationMatch(notification, existingByID)?.readAt
  }));
  const retained = database.notifications.filter(
    (notification) =>
      !LEGACY_DEMO_NOTIFICATION_IDS.has(notification.id) &&
      !(
        notification.workspaceID === workspace.id &&
        notification.userID === user.id &&
        (notification.source === "system" || notification.id.startsWith(SYSTEM_NOTIFICATION_PREFIX))
      )
  );

  return {
    ...database,
    notifications: [...generatedWithReadState, ...retained].sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt))
  };
}

export function generateSystemNotifications(database: ReOSDatabase, now = new Date()): ReOSNotification[] {
  const workspace = activeWorkspace(database);
  const user = currentUser(database);
  if (!workspace || !user) {
    return [];
  }

  const items = workspaceItems(database);
  const listings = workspaceListings(database);
  const sales = workspaceSales(database);
  const itemByID = new Map(items.map((item) => [item.id, item]));
  const notifications: ReOSNotification[] = [];

  listings.forEach((listing) => {
    const item = itemByID.get(listing.itemID);
    if (!item) {
      return;
    }
    if (item.status === "sold" || item.status === "archived") {
      return;
    }

    if (listing.status === "failed") {
      notifications.push(listingFailedNotification(workspace.id, user.id, item, listing));
      return;
    }

    if (listing.status === "ready" || listing.status === "draft") {
      notifications.push(listingReadyNotification(workspace.id, user.id, item, listing));
    }
  });

  const itemsWithFailedListings = new Set(listings.filter((listing) => listing.status === "failed").map((listing) => listing.itemID));
  items
    .filter((item) => item.status !== "sold" && item.status !== "archived")
    .filter((item) => !itemsWithFailedListings.has(item.id))
    .filter((item) => stockReviewIsDue(item, now))
    .forEach((item) => {
      const listing = listingForItemTarget(item.id, listings);
      notifications.push(agingStockNotification(workspace.id, user.id, item, listing, now));
    });

  sales.forEach((sale) => {
    if (sale.spreadsheetSource?.historical) return;
    const item = itemByID.get(sale.itemID);
    if (!item) {
      return;
    }

    if (["sold", "paid", "packed"].includes(sale.status)) {
      notifications.push(shippingDueNotification(workspace.id, user.id, item, sale));
    }

    const activeCopies = listings.filter((listing) => listing.itemID === sale.itemID && listing.id !== sale.listingID && listing.status === "live");
    if (activeCopies.length > 0 && item.quantity <= 0 && !["cancelled", "refunded"].includes(sale.status) && !notifications.some((notice) => notice.type === "cross_listing_conflict" && notice.itemID === item.id)) {
      notifications.push(crossListingConflictNotification(workspace.id, user.id, item, sale, activeCopies));
    }
  });

  items.forEach((item) => {
    if (item.status === "sold" || item.status === "archived") return;
    const run = item.research?.run;
    const conditionPending = item.research?.review?.conditionPending === true && researchConditionPending(item.research, item);
    if (run && (item.research?.status !== "refined" || conditionPending) && ["ready", "failed", "needs_input"].includes(run.status)) {
      const type = run.status === "failed" ? "research_failed" : ((run.status === "needs_input" && run.focus !== "price_position") || conditionPending) ? "research_needs_input" : "research_ready";
      notifications.push({ id: systemNotificationID(user.id, `${type}_${run.id}`), workspaceID: workspace.id, userID: user.id, source: "system", type, itemID: item.id,
        title: conditionPending ? "Check item condition" : type === "research_failed" ? "Retry research" : type === "research_needs_input" ? "Answer a research question" : "Review research",
        body: conditionPending ? "Check the item before relying on its price estimate." : type === "research_failed" ? "Open the research run to see what failed and retry." : type === "research_needs_input" ? "Your input is needed to continue." : "Findings are ready for your review.", createdAt: run.updatedAt });
    }
    const live = listings.filter((listing) => listing.itemID === item.id && listing.status === "live");
    if (item.quantity === 0 && live.length && !notifications.some((notice) => notice.type === "cross_listing_conflict" && notice.itemID === item.id)) {
      notifications.push({ id: systemNotificationID(user.id, `no_stock_${item.id}`), workspaceID: workspace.id, userID: user.id, source: "system", type: "cross_listing_conflict", itemID: item.id, listingID: live[0].id, title: "End listings with no stock", body: `${live.length} live listings have no available stock. End them on the marketplace, then update Listwork.`, createdAt: item.updatedAt });
    }
  });
  for (const account of database.marketplaceAccounts.filter((entry) => entry.workspaceID === workspace.id && entry.connectionStatus === "connected" && ["ebay", "etsy"].includes(entry.marketplace))) {
    const observations = (database.channelObservations ?? []).filter((entry) => entry.workspaceID === workspace.id && entry.marketplaceAccountID === account.id && entry.generation === (account.generation ?? 0));
    const groups = [
      { tab: "new" as const, title: `Review new ${marketplaceLabels[account.marketplace]} listings`, entries: observations.filter((entry) => isNewChannelListing(database, entry)) },
      { tab: "photos" as const, title: `Import missing ${marketplaceLabels[account.marketplace]} photos`, entries: observations.filter((entry) => needsChannelPhotos(database, entry)) },
      { tab: "stock" as const, title: `Check ${marketplaceLabels[account.marketplace]} stock differences`, entries: observations.filter((entry) => hasStockDifference(database, entry)) },
      { tab: "activity" as const, title: `Review ${marketplaceLabels[account.marketplace]} draft changes`, entries: observations.filter((entry) => channelDraftDifferences(database, entry).length > 0) }
    ];
    for (const group of groups) if (group.entries.length) notifications.push({
      id: systemNotificationID(user.id, `channel_${account.id}_${account.generation ?? 0}_${group.tab}`),
      workspaceID: workspace.id, userID: user.id, source: "system", type: "channel_activity",
      title: group.title, body: `${group.entries.length} ${group.entries.length === 1 ? "listing" : "listings"} to review.`, groupCount: group.entries.length,
      channelActivity: { accountID: account.id, tab: group.tab }, createdAt: group.entries.reduce((latest, entry) => entry.detectedAt > latest ? entry.detectedAt : latest, account.createdAt)
    });
  }
  return notifications;
}

function listingReadyNotification(workspaceID: string, userID: string, item: InventoryItem, listing: MarketplaceListing): ReOSNotification {
  const marketplace = marketplaceLabels[listing.marketplace];
  const isDraft = listing.status === "draft";
  return {
    id: systemNotificationID(userID, `listing_ready_${listing.id}`),
    workspaceID,
    userID,
    source: "system",
    type: "listing_ready",
    title: isDraft ? `Finish ${marketplace} draft` : `Review ${marketplace} listing`,
    body: isDraft
      ? `The ${marketplace} draft for ${item.title} needs the missing details finished before you post it on the marketplace.`
      : `The ${marketplace} copy for ${item.title} is ready to review before you list it on the marketplace.`,
    itemID: item.id,
    listingID: listing.id,
    createdAt: listing.updatedAt
  };
}

function listingFailedNotification(workspaceID: string, userID: string, item: InventoryItem, listing: MarketplaceListing): ReOSNotification {
  const warning = listing.validationWarnings[0];
  return {
    id: systemNotificationID(userID, `listing_failed_${listing.id}`),
    workspaceID,
    userID,
    source: "system",
    type: "listing_failed",
    title: `Fix ${marketplaceLabels[listing.marketplace]} listing`,
    body: warning ? `${item.title}: ${warning}` : `${item.title} could not be prepared. Review the listing and try again.`,
    itemID: item.id,
    listingID: listing.id,
    createdAt: listing.updatedAt
  };
}

function agingStockNotification(
  workspaceID: string,
  userID: string,
  item: InventoryItem,
  listing: MarketplaceListing | undefined,
  now: Date
): ReOSNotification {
  return {
    id: systemNotificationID(userID, `aging_stock_${item.id}`),
    workspaceID,
    userID,
    source: "system",
    type: "aging_stock",
    title: "Review aging stock",
    body: inventoryAge(item, now)?.basis === "acquired" ? `${item.title} has been in stock for ${inventoryAgeDays(item, now)} days. Review pricing, photos, or relist it.` : `${item.title}: ${inventoryAgeLabel(item, now)}. Review pricing, photos, or relist it.`,
    itemID: item.id,
    listingID: listing?.id,
    createdAt: thresholdDate(inventoryAge(item, now)?.date ?? now.toISOString(), 60, now)
  };
}

function shippingDueNotification(workspaceID: string, userID: string, item: InventoryItem, sale: Sale): ReOSNotification {
  const action = shippingDueActionCopy(sale.status);
  return {
    id: systemNotificationID(userID, `shipping_due_${sale.id}`),
    workspaceID,
    userID,
    source: "system",
    type: "shipping_due",
    title: `${action.titleVerb} ${item.title}`,
    body: `${marketplaceLabels[sale.marketplace]} order is ${saleStatusLabels[sale.status].toLowerCase()}. ${action.body}`,
    itemID: item.id,
    listingID: sale.listingID,
    saleID: sale.id,
    createdAt: sale.soldAt
  };
}

function shippingDueActionCopy(status: Sale["status"]) {
  if (status === "sold") {
    return {
      titleVerb: "Confirm payment for",
      body: "Confirm payment before packing this order."
    };
  }

  if (status === "packed") {
    return {
      titleVerb: "Ship",
      body: "Add tracking or choose no tracking, then mark it shipped."
    };
  }

  return {
    titleVerb: "Pack",
    body: "Pack it, then update the order status."
  };
}

function crossListingConflictNotification(
  workspaceID: string,
  userID: string,
  item: InventoryItem,
  sale: Sale,
  activeCopies: MarketplaceListing[]
): ReOSNotification {
  const firstCopy = activeCopies[0];
  return {
    id: systemNotificationID(userID, `cross_listing_conflict_${sale.id}`),
    workspaceID,
    userID,
    source: "system",
    type: "cross_listing_conflict",
    title: "Delist other platforms",
    body: `${item.title} sold on ${marketplaceLabels[sale.marketplace]}. ${pluralize(activeCopies.length, "other listing")} may still be active.`,
    itemID: item.id,
    listingID: firstCopy?.id,
    saleID: sale.id,
    createdAt: sale.soldAt
  };
}

function listingForItemTarget(itemID: string, listings: MarketplaceListing[]) {
  const itemListings = listings.filter((listing) => listing.itemID === itemID);
  return (
    itemListings.find((listing) => listing.status === "live") ??
    itemListings.find((listing) => listing.status === "ready") ??
    itemListings.find((listing) => listing.status === "draft") ??
    itemListings[0]
  );
}

function thresholdDate(dateString: string, days: number, now: Date) {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return now.toISOString();
  }
  date.setDate(date.getDate() + days);
  return date > now ? now.toISOString() : date.toISOString();
}

function pluralize(count: number, singular: string) {
  return `${count} ${singular}${count === 1 ? "" : "s"}`;
}

function firstNotificationMatch(notification: Pick<ReOSNotification, "id" | "userID">, existingByID: Map<string, ReOSNotification>) {
  for (const key of userNotificationKeys(notification)) {
    const match = existingByID.get(key);
    if (match) {
      return match;
    }
  }
  return undefined;
}

function userNotificationKeys(notification: Pick<ReOSNotification, "id" | "userID">) {
  return [`${notification.userID}:${notification.id}`, ...legacyUserNotificationIDs(notification).map((id) => `${notification.userID}:${id}`)];
}

function legacyUserNotificationIDs(notification: Pick<ReOSNotification, "id" | "userID">) {
  const userScopedPrefix = `${SYSTEM_NOTIFICATION_PREFIX}${notificationIDPart(notification.userID)}_`;
  if (!notification.id.startsWith(userScopedPrefix)) {
    return [];
  }
  return [`${SYSTEM_NOTIFICATION_PREFIX}${notification.id.slice(userScopedPrefix.length)}`];
}

function systemNotificationID(userID: string, stableKey: string) {
  return `${SYSTEM_NOTIFICATION_PREFIX}${notificationIDPart(userID)}_${stableKey}`;
}

function notificationIDPart(value: string) {
  return value.replace(/[^a-zA-Z0-9._-]+/g, "-").replace(/^-+|-+$/g, "") || "user";
}
